<template>
  <div class="c-quiz-question text-primary">
    <QuizQuestionMobileHeader
      v-if="isMobile"
      :data="{
        bullets,
        currentNumberQuestion,
        questionDetails,
        timeLeftToAnswer,
        collectedPoints
      }"
    />
    <QuizQuestionDesktopHeader
      v-if="isDesktop || isTablet"
      :data="{
        bullets,
        currentNumberQuestion,
        collectedPoints,
        questionDetails
      }"
    />
    <div class="question">
      <div class="question__title-cnt">
        <h2 class="question__title">{{ currentQuestion.question }}</h2>
        <Timer
          v-if="isDesktop || isTablet"
          :maxTime="questionDetails.seconds"
          :currentTime="intervalTime"
        />
      </div>
      <ul class="questions__list">
        <li
          class="question__item"
          v-for="answer in currentQuestion.answers"
          :key="answer.id"
        >
          <button
            type="button"
            class="btn question__answer btn-outline-primary text-primary"
            :class="[answer.circleStatus]"
            :style="{
              backgroundColor: answer.selected
                ? colors.primaryOpacity
                : 'transparent'
            }"
            @click="saveAnswer(answer)"
          >
            <Bullet
              v-if="answer.circleStatus"
              class="question__status"
              :mode="answer.circleStatus"
            />
            {{ answer.text }}
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Bullet from "../common/Bullet";
import Timer from "../common/Timer";
import QuizQuestionMobileHeader from "./QuizQuestionMobileHeader";
import QuizQuestionDesktopHeader from "./QuizQuestionDesktopHeader";

import { MissionService } from "@/common/services/api.service";
import { Mutations } from "@/store/global/global.types";

import { mapGetters } from "vuex";

export default {
  name: "QuizQuestion",
  props: ["questionDetails"],
  components: {
    Bullet,
    Timer,
    QuizQuestionMobileHeader,
    QuizQuestionDesktopHeader
  },
  data() {
    return {
      bullets: [],
      collectedPoints: 0,
      timeLeftToAnswer: 0,
      currentNumberQuestion: 0,
      intervalTime: 0,
      currentQuestion: {},
      canSendQuestion: true
    };
  },
  computed: {
    ...mapGetters(["colors", "isTablet", "isDesktop", "isMobile"])
  },
  provide() {
    return {
      bullets: this.bullets
    };
  },
  created() {
    this.interval = null;
  },
  async mounted() {
    try {
      this.$store.commit(Mutations.SHOW_SPINNER);
      await MissionService.startQuizQuestions(this.$route.params.instantwinId);
      await this.getQuestion();
      this.bullets = Array(this.questionDetails.total_questions).fill({
        status: "default"
      });
      this.$store.commit(Mutations.HIDE_SPINNER);
      this.bullets[0] = { status: "current" };
      this.intervalTime = this.questionDetails.seconds;
      this.startTimeout();
    } catch {
      this.$router.push("/dashboard");
    }
  },
  methods: {
    startTimeout() {
      clearInterval(this.interval);
      this.interval = setInterval(async () => {
        this.intervalTime = this.intervalTime - 0.1;
        if (this.intervalTime <= 0) {
          clearInterval(this.interval);
          this.bullets = this.bullets.map((el, i) =>
            i === this.currentNumberQuestion ? { status: "wrong" } : el
          );
          this.currentNumberQuestion++;
          this.bullets = this.bullets.map((el, i) =>
            i === this.currentNumberQuestion ? { status: "current" } : el
          );
          this.intervalTime = this.questionDetails.seconds;
          await this.getQuestion();
          return;
        }
        this.timeLeftToAnswer = Math.floor(this.intervalTime);
      }, 100);
    },
    async getQuestion() {
      const { instantwinId } = this.$route.params;
      if (!instantwinId) {
        return;
      }

      this.$store.commit(Mutations.SHOW_SPINNER);
      const response = await MissionService.getQuizQuestion(instantwinId);
      this.$store.commit(Mutations.HIDE_SPINNER);

      //if there is no response and we are moving to next step
      if (!response.data.data || !response.data.data.answers) {
        const res = await MissionService.getQuizPoints(instantwinId);
        let points = 0;
        if (res.data.data) {
          points = Number(res.data.data);
        }
        this.collectedPoints = points; // Here, the complete quiz points are returned in "points"
        this.$emit("moveToNextStep");
        console.log(this.bullets);
        console.log(this.questionDetails);
        this.$emit("allQuestions", {
          points: this.collectedPoints,
          correctQuestions: this.bullets.filter(el => el.status === "correct")
            .length,
          allQuestions: this.questionDetails.total_questions
        });
        return;
      }
      this.currentQuestion = response.data.data;
      this.intervalTime = this.questionDetails.seconds;
      this.startTimeout();
    },
    async saveAnswer(answer) {
      try {
        if (!this.canSendQuestion) {
          return;
        }

        this.canSendQuestion = false;
        clearInterval(this.interval);

        this.currentQuestion.answers = this.currentQuestion.answers.map(el =>
          el.id === answer.id
            ? { ...answer, selected: true }
            : { ...el, selected: false }
        );

        this.$store.commit(Mutations.SHOW_SPINNER);
        const result = await MissionService.sendQuizAnswer(
          this.$route.params.instantwinId,
          {
            answer: answer.id,
            seconds: this.timeLeftToAnswer
          }
        );
        this.$store.commit(Mutations.HIDE_SPINNER);
        this.collectedPoints = this.collectedPoints + result.data.data.points; //Adding single question points

        if (result.data.data.correct) {
          this.bullets = this.bullets.map((el, i) =>
            i === this.currentNumberQuestion ? { status: "correct" } : el
          );
          this.currentQuestion.answers = this.currentQuestion.answers.map(el =>
            el.id === answer.id
              ? { ...el, circleStatus: "primary-correct" }
              : el
          );
        } else {
          this.bullets = this.bullets.map((el, i) =>
            i === this.currentNumberQuestion ? { status: "wrong" } : el
          );
          this.currentQuestion.answers = this.currentQuestion.answers.map(el =>
            el.id === answer.id ? { ...el, circleStatus: "wrong" } : el
          );
        }

        //current number can not be higher than total bullets
        if (
          this.currentNumberQuestion + 1 <
          this.questionDetails.total_questions
        ) {
          this.currentNumberQuestion++;
        }
        await this.waitTimeout(2000);

        await this.getQuestion();

        this.bullets = this.bullets.map((el, i) =>
          i === this.currentNumberQuestion ? { status: "current" } : el
        );
        this.canSendQuestion = true;
      } catch {
        this.$router.push("/dashboard");
      }
    },
    waitTimeout(time) {
      return new Promise(res =>
        setTimeout(() => {
          res();
        }, time)
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.c-quiz-question {
  padding: 20px 15px;
}

.question__title-cnt {
  margin-bottom: 10px;

  @media (min-width: 767px) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 30px;
  }
  @media (min-width: 992px) {
    margin-bottom: 50px;
  }
}

// QUESTION LIST
.question__title {
  font-size: 22px;
  line-height: 2;
  text-align: center;
  font-weight: bold;
}

.questions__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.question__item {
  margin-bottom: 10px;
}

.question__answer {
  display: block;
  width: 100%;
  text-align: center;
  font-weight: bold;
  position: relative;
  border-radius: 8px;
  padding: 8px 40px 8px 20px;
}

.wrong {
  border-color: #f54f4f;
}

.question__status {
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
}
</style>
